import {
  BookkeepingActionFilter as BookkeepingActionFilterProto,
  type BookkeepingPendingAction as BookkeepingPendingActionProto,
  ConfirmManagedPlatformAccessRequest,
  GetBookkeepingPendingActionAggregationsRequest,
  ListBookkeepingPendingActionsRequest,
} from '@taxfyle/api-internal/internal/bookkeeping_pb'
import { collection } from '@/infra/collection'
import {
  BookkeepingPendingAction,
  type BookkeepingManagerAction,
  type BookkeepingPendingActionProps,
} from '@/features/admin/bookkeeping/BookkeepingPendingAction'
import {
  mapAccessMethodProtoToBookkeepingAccessMethod,
  mapAccessStatusProtoToBookkeepingManagerAction,
  mapPlatformConnectionProtoToProps,
  mapBookkeepingPendingActionSortFieldToOrderBy,
} from '@/features/admin/bookkeeping/bookkeeping-pending-action-mappers'
import { type AdminApi } from '@/features/admin/api/admin-api'
import type { PrimeMemberByPublicIdFn } from '@/features/iam'
import { Id } from '@/infra/id'
import { Member } from '@/features/iam/members/Member'
import { type CursorPaged, type CursorPageQuery } from '@/infra/paging'
import { safeTimestampToDate } from '@/utils/grpc-util'
import { filter, map } from 'rxjs'
import { RealtimeEvent } from '@taxfyle/api-internal/internal/realtime_pb'

import TypeCase = RealtimeEvent.TypeCase

/**
 * The fields bookkeeping pending actions can be sorted by.
 */
export type BookkeepingPendingActionSortField = 'DatePendingSince' | 'Id'

/**
 * The direction to sort bookkeeping pending actions in; `none` leaves the
 * order to the server.
 */
export type BookkeepingPendingActionSortDirection = 'asc' | 'desc' | 'none'

export interface ListBookkeepingPendingActionsQuery extends CursorPageQuery {
  workspaceIds?: Id[]
  sort?: {
    field: BookkeepingPendingActionSortField
    direction: BookkeepingPendingActionSortDirection
  }
  searchText?: string
  action?: BookkeepingManagerAction
}

export interface FetchBookkeepingPendingActionAggregationsQuery {
  workspaceIds?: Id[]
}

export interface BookkeepingPendingActionAggregations {
  total: number
  confirmAccessTotal: number
}

export class BookkeepingManagerStore {
  /**
   * Bookkeeping events from the realtime channel.
   */
  readonly events$

  /**
   * Bookkeeping pending actions collection.
   */
  readonly bookkeepingPendingActions = collection<
    BookkeepingPendingAction,
    Id,
    BookkeepingPendingActionProps
  >({
    getDataId: (d) => d.id,
    getModelId: (m) => m.id,
    create: () => new BookkeepingPendingAction(),
  })

  constructor(
    private readonly _api: AdminApi,
    private readonly _primeMember: PrimeMemberByPublicIdFn,
  ) {
    this.events$ = this._api.realtime$.pipe(
      filter((event) => event.getTypeCase() === TypeCase.BOOKKEEPING_UPDATED),
      map((x) => x.getBookkeepingUpdated()?.getProgressionInfo()?.getId()),
    )
  }

  /**
   * Lists bookkeeping pending actions.
   *
   * @param query
   * @param signal
   */
  async listBookkeepingPendingActions(
    query: ListBookkeepingPendingActionsQuery,
    signal?: AbortSignal,
  ): Promise<CursorPaged<BookkeepingPendingAction>> {
    const request = new ListBookkeepingPendingActionsRequest()

    if (query.workspaceIds) {
      request.setWorkspaceIdsList(query.workspaceIds.map(Id.assert))
    }

    if (query.sort) {
      const { field, direction } = query.sort
      request.setOrderBy(
        `${mapBookkeepingPendingActionSortFieldToOrderBy(field)} ${direction !== 'none' ? direction : ''}`,
      )
    }

    if (query.limit) {
      request.setPageSize(query.limit)
    }

    if (query.after) {
      request.setPageToken(query.after)
    }

    switch (query.action) {
      case 'ConfirmAccess':
        request.setActionFilter(BookkeepingActionFilterProto.CONFIRM_ACCESS)
        break
      case 'Unspecified':
      case undefined:
        request.setActionFilter(BookkeepingActionFilterProto.ALL)
        break
    }

    if (query.searchText) {
      request.setSearchText(query.searchText)
    }

    const result =
      await this._api.bookkeeping.listBookkeepingPendingActions(request)
    signal?.throwIfAborted()

    return {
      next: result.getNextPageToken(),
      data: this.bookkeepingPendingActions.set(
        result
          .getBookkeepingPendingActionsList()
          .map((proto) => this._mapBookkeepingPendingActionProtoToProps(proto)),
      ),
    }
  }

  /**
   * Fetches bookkeeping pending action aggregations.
   *
   * @param query
   * @param signal
   */
  async fetchBookkeepingPendingActionAggregations(
    { workspaceIds }: FetchBookkeepingPendingActionAggregationsQuery,
    signal?: AbortSignal,
  ): Promise<BookkeepingPendingActionAggregations> {
    const request = new GetBookkeepingPendingActionAggregationsRequest()

    if (workspaceIds) {
      request.setWorkspaceIdsList(workspaceIds.map(Id.assert))
    }

    const response =
      await this._api.bookkeeping.getBookkeepingPendingActionAggregations(
        request,
      )
    signal?.throwIfAborted()

    return {
      total: response.getTotal(),
      confirmAccessTotal: response.getConfirmAccessTotal(),
    }
  }

  /**
   * Confirms managed platform access.
   */
  async confirmManagedPlatformAccess(id: Id): Promise<void> {
    const assertedId = Id.assert(id)
    const request = new ConfirmManagedPlatformAccessRequest().setId(assertedId)

    await this._api.bookkeeping.confirmManagedPlatformAccess(request)

    this.bookkeepingPendingActions.remove(assertedId)
  }

  private _mapBookkeepingPendingActionProtoToProps(
    proto: BookkeepingPendingActionProto,
  ): BookkeepingPendingActionProps {
    const workspaceId = proto.getWorkspaceId()
    const client = proto.getClient()
    const championProMember = proto.getChampionPro()?.getMember()
    const platformConnection = proto.getPlatformConnection()

    return {
      id: proto.getId(),
      jobShortId: proto.getShortId(),
      workspaceId,
      customer: client
        ? this._primeMember({
            userPublicId: client.getUserPublicId(),
            workspaceId,
          })
        : Member.none,
      championPro: championProMember
        ? this._primeMember({
            userPublicId: championProMember.getUserPublicId(),
            workspaceId,
          })
        : Member.none,
      jobName: proto.getJobName(),
      platformConnection: platformConnection
        ? mapPlatformConnectionProtoToProps(platformConnection)
        : undefined,
      accessMethod: mapAccessMethodProtoToBookkeepingAccessMethod(
        proto.getAccessMethod(),
      ),
      action: mapAccessStatusProtoToBookkeepingManagerAction(
        proto.getAccessStatus(),
      ),
      dateCreated: safeTimestampToDate(proto.getDateCreated()),
      datePendingSince: safeTimestampToDate(proto.getDatePendingSince()),
    }
  }
}
